import { CheckboxEditor, Field, TextEditor } from '@approvalmax/ui';
import { Fade } from '@approvalmax/ui/src/components';
import { constants } from 'modules/common';
import { backend } from 'modules/data';
import { memo, useCallback } from 'react';
import bemFactory from 'react-bem-factory';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import RequestFormRow from '../../../../../components/RequestFormRow/RequestFormRow';
import RequestFormRowGroup from '../../../../../components/RequestFormRowGroup/RequestFormRowGroup';
import RequestFormSection from '../../../../../components/RequestFormSection/RequestFormSection';
import { useQBOVendorRequestContext } from '../../QBOVendorRequestContext';
import { transform } from './PaymentsSection.helpers';
import { messages } from './PaymentsSection.messages';

const { qBooksConstants } = constants;

const qa = bemFactory.qa('reqf-qbooks-vendor-request__payments');

export const PaymentsSection = memo(() => {
    const company = useCompanyBasedOnUrl();
    const { vendor, showErrors, changeVendorDeepProperty, changeVendorProperty } = useQBOVendorRequestContext();

    const showSpecificFields = Boolean(vendor.paymentDetails);
    const isAUCompany = company.integration?.countryCode === backend.OrganisationVersion.QBO_AU;

    const isInvalidBusinessNumber = !!(
        vendor.businessNumber &&
        (!vendor.businessNumber?.trim() || vendor.businessNumber.length > qBooksConstants.BUSINESS_NUMBER_MAX_LENGTH)
    );
    const isInvalidBankAccountName = showSpecificFields && !vendor.paymentDetails?.bankAccountName?.trim();
    const isInvalidBankBranchIdentifier =
        showSpecificFields &&
        vendor.paymentDetails?.bankBranchIdentifier?.trim().length !==
            qBooksConstants.BANK_BRANCH_IDENTIFIER_MAX_LENGTH;
    const isInvalidBankAccountNumber = showSpecificFields && !vendor.paymentDetails?.bankAccountNumber?.trim();
    const isInvalidStatementText = showSpecificFields && !vendor.paymentDetails?.statementText?.trim();

    const handleSpecifyPaymentDetails = useCallback(
        (value: boolean) => {
            changeVendorProperty('paymentDetails')(value ? {} : null);
        },
        [changeVendorProperty]
    );

    return (
        <RequestFormSection header={messages.payments}>
            <RequestFormRowGroup>
                <RequestFormRow>
                    <Field
                        css='max-width: 50%;
                        min-width: 50%;
                        padding-right: 10px;'
                        title={messages.acctNum}
                    >
                        <TextEditor
                            qa={qa('accountNumber')}
                            maxLength={qBooksConstants.BUSINESS_NUMBER_MAX_LENGTH}
                            value={vendor.accountNumber || ''}
                            onChange={changeVendorProperty('accountNumber')}
                            placeholder={messages.acctNumPlaceholder}
                            invalid={showErrors && isInvalidBusinessNumber}
                        />
                    </Field>
                </RequestFormRow>

                {isAUCompany && (
                    <RequestFormRow>
                        <CheckboxEditor
                            qa={qa('specifyPaymentsDetails')}
                            value={showSpecificFields}
                            onChange={handleSpecifyPaymentDetails}
                        >
                            {messages.specifyPaymentsDetails}
                        </CheckboxEditor>
                    </RequestFormRow>
                )}

                <Fade in={showSpecificFields}>
                    <RequestFormRow>
                        <Field required title={messages.bankAccountName}>
                            <TextEditor
                                qa={qa('bankAccountName')}
                                maxLength={qBooksConstants.BANK_ACCOUNT_NAME_MAX_LENGTH}
                                value={vendor.paymentDetails?.bankAccountName || ''}
                                onChange={changeVendorDeepProperty('paymentDetails', 'bankAccountName')}
                                placeholder={messages.bankAccountNamePlaceholder}
                                invalid={showErrors && isInvalidBankAccountName}
                            />
                        </Field>

                        <Field required title={messages.bsb}>
                            <TextEditor
                                qa={qa('bankBranchIdentifier')}
                                maxLength={qBooksConstants.BANK_BRANCH_IDENTIFIER_MAX_LENGTH}
                                value={vendor.paymentDetails?.bankBranchIdentifier || ''}
                                onChange={changeVendorDeepProperty('paymentDetails', 'bankBranchIdentifier')}
                                placeholder={messages.bsbPlaceholder}
                                invalid={showErrors && isInvalidBankBranchIdentifier}
                                transform={transform}
                            />
                        </Field>
                    </RequestFormRow>

                    <RequestFormRow>
                        <Field required title={messages.bankAccountNumber}>
                            <TextEditor
                                qa={qa('bankAccountNumber')}
                                maxLength={qBooksConstants.BANK_ACCOUNT_NUMBER_MAX_LENGTH}
                                value={vendor.paymentDetails?.bankAccountNumber || ''}
                                onChange={changeVendorDeepProperty('paymentDetails', 'bankAccountNumber')}
                                placeholder={messages.bankAccountNumberPlaceholder}
                                invalid={showErrors && isInvalidBankAccountNumber}
                            />
                        </Field>

                        <Field required title={messages.statement}>
                            <TextEditor
                                qa={qa('statementText')}
                                maxLength={qBooksConstants.STATEMENT_MAX_LENGTH}
                                value={vendor.paymentDetails?.statementText || ''}
                                onChange={changeVendorDeepProperty('paymentDetails', 'statementText')}
                                placeholder={messages.statementPlaceholder}
                                invalid={showErrors && isInvalidStatementText}
                            />
                        </Field>
                    </RequestFormRow>
                </Fade>
            </RequestFormRowGroup>
        </RequestFormSection>
    );
});

PaymentsSection.displayName = 'PaymentsSection';
